// src/features/reporting/pages/inventory/StockValuePage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography, useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';
import PageHeader from '../../../../components/common/PageHeader';
import { useApi } from '../../../../hooks/api/useApi';
import { formatDKK } from '../../../../utils/formatters/currencyFormatter';
import { usePermissions } from '../../../auth/hooks/usePermissions';
import { useProductGroups } from '../../../inventory/hooks/useProductGroups';
import { inventoryAnalyticsAPI } from '../../services/inventoryAnalytics';
import { withInfo } from '../../../../components/common/InfoTip';
import {
  ChartCard, FilterRow, PeriodSelect, SERIES_COLORS, StatRow, StatTile, formatCompactDKK, periodDates,
} from '../../components/analysisParts';

const granularityFor = (days) => (days <= 92 ? 'day' : days <= 400 ? 'week' : 'month');

/** Name each line at its last point, so identity never rests on colour alone. */
function EndLabel({ x, y, index, count, text, color, dy = 4 }) {
  if (index !== count - 1) return null;
  return (
    <text x={x + 8} y={y} dy={dy} fill={color} fontSize={12} fontWeight={600}>{text}</text>
  );
}

/**
 * Vertical offset per end label. The two values are often within a few
 * percent of each other, so the labels would print on top of one another:
 * then the higher line's label goes above its end and the lower one's below.
 */
const endLabelOffsets = (points, keys) => {
  const last = points[points.length - 1];
  if (!last) return {};
  const max = Math.max(...points.flatMap((p) => keys.map((k) => p[k])), 1);
  const [a, b] = keys;
  if (Math.abs(last[a] - last[b]) / max >= 0.06) return { [a]: 4, [b]: 4 };
  return last[a] > last[b] ? { [a]: -4, [b]: 14 } : { [a]: 14, [b]: -4 };
};

export default function StockValuePage() {
  const { t } = useTranslation(['reporting', 'common']);
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasScope } = usePermissions();
  const canFilterGroups = hasScope('inventory:product-group:read');
  const { productGroups, fetchProductGroups } = useProductGroups();
  const { loading, error, execute, clearError } = useApi();

  const [days, setDays] = useState(90);
  const [productGroupId, setProductGroupId] = useState('');
  const [view, setView] = useState('chart');
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (canFilterGroups) fetchProductGroups();
  }, [canFilterGroups, fetchProductGroups]);

  const load = useCallback(async () => {
    const result = await execute(() => inventoryAnalyticsAPI.getValueHistory({
      ...periodDates(days),
      granularity: granularityFor(days),
      productGroupId,
    }));
    if (result.success) setHistory(result.data.data);
  }, [execute, days, productGroupId]);

  useEffect(() => { load(); }, [load]);

  const points = useMemo(() => history?.points ?? [], [history]);
  const first = points[0];
  const last = points[points.length - 1];
  const change = first && last ? last.book_value - first.book_value : null;
  const dateLabel = (value) => format(parseISO(value), days > 92 ? 'd. MMM yy' : 'd. MMM', { locale: da });

  const series = [
    { key: 'book_value', name: t('reporting:stock_value.book_value'), color: SERIES_COLORS[0] },
    { key: 'replacement_value', name: t('reporting:stock_value.replacement_value'), color: SERIES_COLORS[1] },
  ];
  const labelOffsets = endLabelOffsets(points, series.map((s) => s.key));

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <PageHeader
        onBack={() => navigate('/reports')}
        title={t('reporting:stock_value.title')}
        subtitle={t('reporting:stock_value.subtitle')}
      />

      <FilterRow>
        <PeriodSelect value={days} onChange={setDays} />
        {canFilterGroups && (
          <TextField
            select
            size="small"
            label={t('reporting:analysis.product_group')}
            value={productGroupId}
            onChange={(e) => setProductGroupId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">{t('reporting:analysis.all_groups')}</MenuItem>
            {productGroups.map((group) => (
              <MenuItem key={group.product_group_id} value={group.product_group_id}>{group.name}</MenuItem>
            ))}
          </TextField>
        )}
      </FilterRow>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>{error}</Alert>}

      <StatRow>
        <StatTile
          loading={loading && !history}
          label={t('reporting:stock_value.book_value')}
          value={formatDKK(last?.book_value, { whole: true })}
          caption={t('reporting:stock_value.book_value_caption')}
          info={t('reporting:help.stock_value.book_value')}
        />
        <StatTile
          loading={loading && !history}
          label={t('reporting:stock_value.replacement_value')}
          value={formatDKK(last?.replacement_value, { whole: true })}
          caption={t('reporting:stock_value.replacement_caption')}
          info={t('reporting:help.stock_value.replacement_value')}
        />
        <StatTile
          loading={loading && !history}
          label={t('reporting:stock_value.change')}
          value={change == null ? '—' : `${change > 0 ? '+' : ''}${formatDKK(change, { whole: true })}`}
          caption={first ? t('reporting:stock_value.since', { date: dateLabel(first.date) }) : null}
          info={t('reporting:help.stock_value.change')}
        />
        <StatTile
          loading={loading && !history}
          label={t('reporting:stock_value.units')}
          value={last ? last.quantity.toLocaleString('da-DK') : '—'}
          info={t('reporting:help.stock_value.units')}
        />
      </StatRow>

      <ChartCard
        title={t('reporting:stock_value.chart_title')}
        info={t('reporting:help.stock_value.chart')}
        description={history?.valuation_start
          ? t('reporting:stock_value.valued_since', { date: format(parseISO(history.valuation_start), 'd. MMMM yyyy', { locale: da }) })
          : null}
        actions={(
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, next) => next && setView(next)}>
            <ToggleButton value="chart">{t('reporting:analysis.chart')}</ToggleButton>
            <ToggleButton value="table">{t('reporting:analysis.table')}</ToggleButton>
          </ToggleButtonGroup>
        )}
      >
        {!loading && points.length === 0 && (
          <Typography color="text.secondary">{t('reporting:stock_value.empty')}</Typography>
        )}
        {points.length > 0 && view === 'chart' && (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={points} margin={{ top: 8, right: 156, left: 8, bottom: 0 }}>
              <CartesianGrid stroke={theme.palette.divider} vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={dateLabel}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={formatCompactDKK}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
                width={88}
              />
              <Tooltip
                formatter={(value, name) => [formatDKK(value, { whole: true }), name]}
                labelFormatter={(value) => format(parseISO(value), 'd. MMMM yyyy', { locale: da })}
                contentStyle={{
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
                itemStyle={{ color: theme.palette.text.primary }}
              />
              <Legend verticalAlign="top" align="left" height={32} iconType="plainline" />
              {series.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: theme.palette.background.paper }}
                  isAnimationActive={false}
                  label={<EndLabel count={points.length} text={s.name} color={theme.palette.text.secondary} dy={labelOffsets[s.key]} />}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
        {points.length > 0 && view === 'table' && (
          <DataGrid
            rows={[...points].reverse()}
            getRowId={(row) => row.date}
            columns={[
              { field: 'date', headerName: t('reporting:analysis.date'), flex: 1, valueFormatter: (value) => dateLabel(value) },
              withInfo({ field: 'book_value', headerName: series[0].name, flex: 1, type: 'number', valueFormatter: (value) => formatDKK(value) }, t('reporting:help.stock_value.book_value')),
              withInfo({ field: 'replacement_value', headerName: series[1].name, flex: 1, type: 'number', valueFormatter: (value) => formatDKK(value) }, t('reporting:help.stock_value.replacement_value')),
              withInfo({ field: 'quantity', headerName: t('reporting:stock_value.units'), flex: 0.7, type: 'number' }, t('reporting:help.stock_value.units')),
            ]}
            density="compact"
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            autoHeight
            sx={{ border: 'none', '& .MuiDataGrid-cell': { fontVariantNumeric: 'tabular-nums' } }}
          />
        )}
      </ChartCard>

      <Typography variant="body2" color="text.secondary">{t('reporting:stock_value.explanation')}</Typography>
    </Box>
  );
}
